import React from 'react';
import styled from 'astroturf/react';
import { Button } from 'js/design_system/Button';
import { Icon } from 'js/design_system/Icon';
import { Body } from 'js/design_system/Body';
import { Menu } from 'js/design_system/Menu';
import { MenuItem } from 'js/design_system/MenuItem';
import { PopoverMode } from 'js/design_system/Popover';
import type { SortSettings } from './SortPanel';
import { getDefaultSortSettings } from '../utils/sortDefaults';

const FilterBarContainer = styled.div`
  background: var(--background-white);
  border-top: 1px solid var(--stroke-light);
  padding: var(--spacing-xs-16) var(--spacing-sm-24);
`;

const FilterBarContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: var(--spacing-xs-16);
  flex-wrap: wrap;
`;

const Label = styled(Body)`
  color: var(--text-secondary);
  white-space: nowrap;
`;

const YearInputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs-8);
`;

const YearInput = styled.input`
  width: 80px;
  padding: 6px 8px;
  border: 1px solid var(--button-xs-stroke, #141413);
  border-radius: var(--buttons-extra-small-buttons-corner-radius, 8px);
  box-shadow: none;
  font-size: 14px;
  text-align: center;

  &:focus {
    outline: none;
    border-color: var(--stroke-dark);
  }

  &::placeholder {
    color: var(--text-placeholder);
  }
`;

const NavigationContainer = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs-8);
  margin-left: auto;
`;

const ClaimCounter = styled(Body)`
  color: var(--text-primary);
  white-space: nowrap;
  padding: 0 var(--spacing-xs-8);
`;

export type ClaimFilter = 'all' | 'essential' | 'optional';

export interface FilterBarSettings {
  claimFilter: ClaimFilter;
  minYear: number | null;
  maxYear: number | null;
  sortSettings: SortSettings | null;
}

interface FilterBarProps {
  settings: FilterBarSettings;
  onSettingsChange: (settings: FilterBarSettings) => void;
  essentialCount?: number;
  optionalCount?: number;
  currentClaimIndex?: number;
  totalClaims?: number;
  onFirstClaim?: () => void;
  onNextClaim?: () => void;
  onPreviousClaim?: () => void;
}

export const FilterBar: React.FC<FilterBarProps> = ({
  settings,
  onSettingsChange,
  essentialCount = 0,
  optionalCount = 0,
  currentClaimIndex = -1,
  totalClaims = 0,
  onFirstClaim,
  onNextClaim,
  onPreviousClaim,
}) => {
  // Claim filter options
  const claimFilterOptions = [
    {
      value: 'all' as ClaimFilter,
      label: 'All Claims',
      count: essentialCount + optionalCount,
    },
    {
      value: 'essential' as ClaimFilter,
      label: 'Essential Only',
      count: essentialCount,
    },
    {
      value: 'optional' as ClaimFilter,
      label: 'Optional Only',
      count: optionalCount,
    },
  ];

  const getCurrentClaimFilterText = () => {
    if (settings.claimFilter === 'all') return 'All Claims';
    if (settings.claimFilter === 'essential') return 'Essential';
    if (settings.claimFilter === 'optional') return 'Optional';
    return 'All Claims';
  };

  const handleClaimFilterSelect = (filter: ClaimFilter) => {
    onSettingsChange({ ...settings, claimFilter: filter });
  };

  // Year filter handlers - set sensible defaults on first interaction
  const handleMinYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const year = value === '' ? null : parseInt(value, 10);
    if (year !== null && isNaN(year)) return;

    // If this is the first interaction (was null), default to 2020
    if (settings.minYear === null && year !== null) {
      onSettingsChange({ ...settings, minYear: 2020 });
      return;
    }

    onSettingsChange({ ...settings, minYear: year });
  };

  const handleMaxYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const year = value === '' ? null : parseInt(value, 10);
    if (year !== null && isNaN(year)) return;

    // If this is the first interaction (was null), default to current year
    if (settings.maxYear === null && year !== null) {
      onSettingsChange({ ...settings, maxYear: new Date().getFullYear() });
      return;
    }

    onSettingsChange({ ...settings, maxYear: year });
  };

  // Sort options
  const sortOptions = [
    {
      field: 'relevance_score' as const,
      direction: 'desc' as const,
      label: 'Most relevant',
    },
    {
      field: 'citationCount' as const,
      direction: 'desc' as const,
      label: 'Most cited',
    },
    {
      field: 'publicationYear' as const,
      direction: 'desc' as const,
      label: 'Newest first',
    },
    {
      field: 'publicationYear' as const,
      direction: 'asc' as const,
      label: 'Oldest first',
    },
    {
      field: 'impactFactor' as const,
      direction: 'desc' as const,
      label: 'Highest journal impact',
    },
  ];

  const activeSort: SortSettings =
    settings.sortSettings || getDefaultSortSettings();

  const getCurrentSortText = () => {
    const option = sortOptions.find(
      (opt) =>
        opt.field === activeSort.field &&
        opt.direction === activeSort.direction,
    );
    return option?.label || 'Sort';
  };

  const handleSortSelect = (
    field: SortSettings['field'],
    direction: SortSettings['direction'],
  ) => {
    onSettingsChange({ ...settings, sortSettings: { field, direction } });
  };

  return (
    <FilterBarContainer>
      <FilterBarContent>
        <Label size="sm" bold>
          Filter & Sort:
        </Label>

        {/* Claim Filter */}
        <Menu
          mode={PopoverMode.Click}
          placement="bottom-end"
          offset={4}
          render={() => (
            <>
              {claimFilterOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  onClick={() => handleClaimFilterSelect(option.value)}
                >
                  <span>{option.label}</span>
                  <span style={{ marginLeft: 'auto' }}>({option.count})</span>
                </MenuItem>
              ))}
            </>
          )}
        >
          <Button
            size="xs"
            icon={<Icon name="filter_list" size={16} />}
            aria-label={`Filter claims: ${getCurrentClaimFilterText()}`}
          >
            <span>{getCurrentClaimFilterText()}</span>
            <Icon name="expand_more" size={16} style={{ marginLeft: '4px' }} />
          </Button>
        </Menu>

        {/* Year Filter */}
        <YearInputContainer>
          <Label size="sm" bold>
            Year:
          </Label>
          <YearInput
            type="number"
            placeholder="Start"
            value={settings.minYear ?? ''}
            onChange={handleMinYearChange}
            aria-label="Start year"
            min="1000"
            max={new Date().getFullYear().toString()}
          />
          <Body size="sm">—</Body>
          <YearInput
            type="number"
            placeholder="End"
            value={settings.maxYear ?? ''}
            onChange={handleMaxYearChange}
            aria-label="End year"
            min="1000"
            max={new Date().getFullYear().toString()}
          />
        </YearInputContainer>

        {/* Sort */}
        <Menu
          mode={PopoverMode.Click}
          placement="bottom-end"
          offset={4}
          render={() => (
            <>
              {sortOptions.map((option) => (
                <MenuItem
                  key={`${option.field}-${option.direction}`}
                  onClick={() =>
                    handleSortSelect(option.field, option.direction)
                  }
                >
                  {option.label}
                </MenuItem>
              ))}
            </>
          )}
        >
          <Button
            size="xs"
            icon={<Icon name="swap_vert" size={16} />}
            aria-label={`Sort results: ${getCurrentSortText()}`}
          >
            <span>Sort: {getCurrentSortText()}</span>
            <Icon name="expand_more" size={16} style={{ marginLeft: '4px' }} />
          </Button>
        </Menu>

        {/* Claim Navigation */}
        {totalClaims > 0 && (
          <NavigationContainer>
            {currentClaimIndex === -1 ? (
              <Button
                size="xs"
                onClick={onFirstClaim}
                iconRight={<Icon name="arrow_forward" size={16} />}
                aria-label="Go to first claim"
              >
                First claim
              </Button>
            ) : (
              <>
                {currentClaimIndex > 0 && (
                  <Button
                    size="xs"
                    onClick={onPreviousClaim}
                    icon={<Icon name="arrow_back" size={16} />}
                    aria-label="Go to previous claim"
                  >
                    Previous
                  </Button>
                )}
                <ClaimCounter size="sm" bold>
                  {currentClaimIndex + 1} / {totalClaims}
                </ClaimCounter>
                {currentClaimIndex < totalClaims - 1 && (
                  <Button
                    size="xs"
                    onClick={onNextClaim}
                    iconRight={<Icon name="arrow_forward" size={16} />}
                    aria-label="Go to next claim"
                  >
                    Next
                  </Button>
                )}
              </>
            )}
          </NavigationContainer>
        )}
      </FilterBarContent>
    </FilterBarContainer>
  );
};

export default FilterBar;
